import React from 'react';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, Droplets, Sun, Wind } from 'lucide-react';

const TechnicalInfo = () => {
  const growthData = [
    { name: 'Snake Plant', growth: 2, water: 1, light: 2, care: 1 },
    { name: 'ZZ Plant', growth: 1, water: 1, light: 1, care: 1 },
    { name: 'Peace Lily', growth: 4, water: 4, light: 3, care: 3 },
    { name: 'Spider Plant', growth: 5, water: 3, light: 3, care: 2 },
    { name: 'Aloe Vera', growth: 2, water: 1, light: 5, care: 1 },
    { name: 'Lavender', growth: 3, water: 2, light: 5, care: 3 }
  ];

  const careDistribution = [
    { name: 'Low Maintenance', value: 40, color: '#22c55e' },
    { name: 'Medium Care', value: 35, color: '#eab308' },
    { name: 'High Attention', value: 25, color: '#ef4444' }
  ];

  const lightRequirements = [
    { name: 'Low Light', value: 30, color: '#6366f1' },
    { name: 'Indirect Light', value: 45, color: '#8b5cf6' },
    { name: 'Direct Light', value: 25, color: '#f59e0b' }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="flex items-center justify-center mb-4">
            <TrendingUp className="h-8 w-8 text-primary-500 mr-3" />
            <h2 className="text-4xl font-bold text-gray-900">
              Plant Growth Analytics
            </h2>
          </div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Data-driven insights into plant care requirements, growth patterns, and optimal conditions for thriving plants.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {/* Growth Rate Chart */}
          <motion.div
            className="bg-gradient-to-br from-primary-50 to-earth-50 rounded-2xl p-8 shadow-lg"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <TrendingUp className="h-6 w-6 text-primary-500 mr-2" />
              Growth Rate Comparison
            </h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={growthData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="name" 
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    fontSize={12}
                  />
                  <YAxis />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: '#f9fafb',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="growth" fill="#22c55e" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p className="text-sm text-gray-600 mt-4">
              Growth rate scale: 1 (Slow) to 5 (Fast) - measured in inches per month
            </p>
          </motion.div>

          {/* Care Requirements */}
          <motion.div
            className="bg-gradient-to-br from-earth-50 to-primary-50 rounded-2xl p-8 shadow-lg"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <Droplets className="h-6 w-6 text-blue-500 mr-2" />
              Care Level Distribution
            </h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={careDistribution}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}%`}
                  >
                    {careDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap gap-4 mt-4">
              {careDistribution.map((item) => (
                <div key={item.name} className="flex items-center">
                  <div 
                    className="w-4 h-4 rounded mr-2" 
                    style={{ backgroundColor: item.color }}
                  ></div>
                  <span className="text-sm text-gray-600">{item.name}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Stats Cards */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, staggerChildren: 0.1 }}
          viewport={{ once: true }}
        >
          <motion.div className="bg-white rounded-xl p-6 shadow-lg border border-primary-100">
            <div className="flex items-center justify-between mb-4">
              <Sun className="h-8 w-8 text-yellow-500" />
              <span className="text-2xl font-bold text-gray-900">85%</span>
            </div>
            <h4 className="font-semibold text-gray-900 mb-1">Light Adaptation</h4>
            <p className="text-sm text-gray-600">Plants adapt to indoor lighting</p>
          </motion.div>

          <motion.div className="bg-white rounded-xl p-6 shadow-lg border border-blue-100">
            <div className="flex items-center justify-between mb-4">
              <Droplets className="h-8 w-8 text-blue-500" />
              <span className="text-2xl font-bold text-gray-900">92%</span>
            </div>
            <h4 className="font-semibold text-gray-900 mb-1">Survival Rate</h4>
            <p className="text-sm text-gray-600">With proper watering schedule</p>
          </motion.div>

          <motion.div className="bg-white rounded-xl p-6 shadow-lg border border-green-100">
            <div className="flex items-center justify-between mb-4">
              <TrendingUp className="h-8 w-8 text-green-500" />
              <span className="text-2xl font-bold text-gray-900">3.2x</span>
            </div>
            <h4 className="font-semibold text-gray-900 mb-1">Growth Boost</h4>
            <p className="text-sm text-gray-600">With optimal placement</p>
          </motion.div>

          <motion.div className="bg-white rounded-xl p-6 shadow-lg border border-purple-100">
            <div className="flex items-center justify-between mb-4">
              <Wind className="h-8 w-8 text-purple-500" />
              <span className="text-2xl font-bold text-gray-900">78%</span>
            </div>
            <h4 className="font-semibold text-gray-900 mb-1">Air Quality</h4>
            <p className="text-sm text-gray-600">Improvement with plants</p>
          </motion.div>
        </motion.div>

        {/* Light Requirements Chart */}
        <motion.div
          className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl p-8 shadow-lg"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <Sun className="h-6 w-6 text-yellow-500 mr-2" />
            Light Requirements Distribution
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={lightRequirements}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="value"
                    label={({ name, value }) => `${value}%`}
                  >
                    {lightRequirements.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-4">
              {lightRequirements.map((item) => (
                <div key={item.name} className="flex items-center justify-between p-4 bg-white rounded-lg shadow-sm">
                  <div className="flex items-center">
                    <div 
                      className="w-4 h-4 rounded mr-3" 
                      style={{ backgroundColor: item.color }}
                    ></div>
                    <span className="font-medium text-gray-900">{item.name}</span>
                  </div>
                  <span className="text-lg font-bold text-gray-700">{item.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TechnicalInfo;
